import { IComponent } from "../Models/IComponent";
import { IContainerComponent } from "../Models/IContainerComponent";

export type ComponentPath = string[];

export function getComponentAtPath(children: IComponent[] | undefined, path: ComponentPath): IComponent | null {
  if (!children || path.length === 0) {
    return null;
  }

  const [head, ...rest] = path;
  const node = children.find((child) => child.Id === head);
  if (!node) {
    return null;
  }

  if (rest.length === 0) {
    return node;
  }

  return getComponentAtPath((node as IContainerComponent).Children, rest);
}

export function updateComponentAtPath<T extends IContainerComponent>(container: T, path: ComponentPath, updated: IComponent): T {
  if (path.length === 0) {
    return container;
  }

  const updateChildren = (children: IComponent[], remainingPath: ComponentPath): IComponent[] => {
    const [head, ...rest] = remainingPath;
    return children.map((child) => {
      if (child.Id !== head) {
        return child;
      }

      if (rest.length === 0) {
        return updated;
      }

      return {
        ...child,
        Children: updateChildren((child as IContainerComponent).Children ?? [], rest),
      } as IComponent;
    });
  };

  return {
    ...container,
    Children: updateChildren(container.Children ?? [], path),
  };
}

export function updateComponentFromRoot<T extends IContainerComponent>(root: T, path: ComponentPath, updated: IComponent): T {
  if (path.length === 1 && path[0] === root.Id) {
    return updated as T;
  }

  const relativePath = path[0] === root.Id ? path.slice(1) : path;
  return updateComponentAtPath(root, relativePath, updated);
}
